import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MessageService } from '@corelabs/angular-messages';
import { TranslateService } from '@ngx-translate/core';
import { of, catchError, map, mergeMap, tap, switchMap, concatMap } from 'rxjs';
import { finalizeLoader } from '@app/shared/components';
import { ToastErrorService } from '@app/shared/services';
import { reflectionActions } from '../actions/reflection.actions';
import { HttpService } from '../services/http.service';

@Injectable()
export class ReflectionEffects {
    private readonly actions = inject(Actions);
    private readonly httpService = inject(HttpService);
    private readonly messageService = inject(MessageService);
    private readonly translateService = inject(TranslateService);
    private readonly toastErrorService = inject(ToastErrorService);

    getReflection$ = createEffect(() => {
        return this.actions.pipe(
            ofType(reflectionActions.getReflection),
            mergeMap(({ id }) => {
                return this.httpService.getReflection(id).pipe(
                    finalizeLoader('reflection-get'),
                    map((reflection) => reflectionActions.getReflectionSuccess({ payload: reflection })),
                    catchError((response: HttpErrorResponse) => of(reflectionActions.getReflectionError({ code: response.status, id }))),
                );
            }),
        );
    });

    createReflection$ = createEffect(() => {
        return this.actions.pipe(
            ofType(reflectionActions.createReflection),
            switchMap(({ value }) => {
                return this.httpService.createReflection(value).pipe(
                    finalizeLoader('reflection-create'),
                    map((reflection) => reflectionActions.createReflectionSuccess({ payload: reflection })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.reflections.created'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.reflections.createError');
                        return of(reflectionActions.createReflectionError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    updateReflection$ = createEffect(() => {
        return this.actions.pipe(
            ofType(reflectionActions.updateReflection),
            switchMap(({ id, value }) => {
                return this.httpService.updateReflection(id, value).pipe(
                    finalizeLoader('reflection-update'),
                    map((reflection) => reflectionActions.updateReflectionSuccess({ payload: reflection })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.reflections.updated'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.reflections.updateError');
                        return of(reflectionActions.updateReflectionError({ code: response.status }));
                    }),
                );
            }),
        );
    });

    deleteReflection$ = createEffect(() => {
        return this.actions.pipe(
            ofType(reflectionActions.deleteReflection),
            concatMap(({ id }) => {
                return this.httpService.deleteReflection(id).pipe(
                    finalizeLoader('reflection-delete'),
                    map(() => reflectionActions.deleteReflectionSuccess({ id })),
                    tap(() => this.messageService.add(this.translateService.instant('toast.reflections.deleted'), 'success')),
                    catchError((response: HttpErrorResponse) => {
                        this.toastErrorService.show(response, 'toast.reflections.deleteError');
                        return of(reflectionActions.deleteReflectionError({ code: response.status }));
                    }),
                );
            }),
        );
    });
}
